import { ListItemIcon } from '@mui/material';
import { MouseEvent, useRef, useState } from 'react';

import { TypeaheadSelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { HelpCircleIcon } from '@/components/icons/HelpCircleIcon';
import { Stars01Icon } from '@/components/icons/Stars01Icon';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { Tooltip } from '@/components/poppers/Tooltip/Tooltip';
import { useReportError } from '@/hooks/useReportError';
import {
  dataToForm,
  formDataToInput,
} from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/AIOnboardingModalForm.utils';
import { getMissingRequiredEntityFields } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/fields/entities.fields.utils';
import { getMissingRequiredTestamentaryEntityFields } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/fields/testamentaryEntities.fields.utils';
import { useOnboardingPrincipalNodes } from '@/modules/aiOnboarding/AIOnboardingModal/context/useOnboardingPrincipalNodes';
import {
  AiOnboardingModal_HouseholdFragment,
  useAiOnboardingModalBulkCreateMutation,
} from '@/modules/aiOnboarding/AIOnboardingModal/graphql/aiOnboardingModal.generated';
import { AiSuggestionForReviewCard } from '@/modules/aiSuggestions/aiSuggestions.contracts';
import { DispositiveProvisionsPossibleRecipientsDocument } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsFormRecipients/graphql/DispositiveProvisionsFormRecipients.generated';
import { BUSINESS_ENTITY_TYPES } from '@/modules/entities/entities.constants';
import { getEntityTypeFromEntityKind } from '@/modules/entities/utils/getEntityTypeFromEntityKind';
import { COLORS } from '@/styles/tokens/colors';
import { AiSuggestionAcceptanceStatus } from '@/types/schema';
import { englishJoinStringList } from '@/utils/formatting/strings';
import { getNodes } from '@/utils/graphqlUtils';
import { sentenceCaseWithAcronyms } from '@/utils/textFormatting';

import { DispositiveProvisionsSuggestedRecipientsDocument } from '../graphql/SuggestedRecipients.generated';

function getSuggestedRecipientValidationMessage(
  missingFields: string[]
): string | undefined {
  if (missingFields.length === 0) return undefined;

  return `This suggestion needs ${englishJoinStringList(missingFields)}. Go to the Estates page to resolve and accept the suggestion, then return here.`;
}

export function isSuggestedRecipient(
  suggestion: AiSuggestionForReviewCard
): boolean {
  if (
    suggestion.acceptanceStatus ||
    suggestion.onboardedEntity ||
    suggestion.onboardedTestamentaryEntity
  ) {
    return false;
  }
  const kind = suggestion.onboardingEntity?.entityKind;
  if (kind) {
    return !BUSINESS_ENTITY_TYPES.includes(getEntityTypeFromEntityKind(kind));
  }
  return Boolean(suggestion.onboardingTestamentaryEntity);
}

export function useSuggestedRecipientOptions({
  householdId,
  household,
  pendingSuggestions,
  onCreated,
  enabled = true,
  recipientNodeIds = [],
  referenceOnboardingSuggestionId,
}: {
  householdId: string;
  household: AiOnboardingModal_HouseholdFragment | undefined;
  pendingSuggestions: AiSuggestionForReviewCard[];
  onCreated: (id: string) => void;
  enabled?: boolean;
  recipientNodeIds?: string[];
  referenceOnboardingSuggestionId?: string;
}) {
  // Use the recipient's graph identity or explicit onboarding reference.
  // A shared name alone does not establish an association.
  // Offer the first eligible match if more than one pending record is linked.
  const suggestion = pendingSuggestions.find((suggestion) => {
    if (!enabled || !isSuggestedRecipient(suggestion)) return false;
    if (recipientNodeIds.length > 0) {
      return getNodes(suggestion.sourceKgV2Nodes).some((node) =>
        recipientNodeIds.includes(node.id)
      );
    }
    return suggestion.id === referenceOnboardingSuggestionId;
  });
  const { accept, creating, disabled, selectionRevision } =
    useAcceptSuggestedRecipient({
      householdId,
      household,
      suggestion,
      onCreated,
    });

  const options: TypeaheadSelectInputOption<string>[] = [];
  if (suggestion) {
    const entity = suggestion.onboardingEntity;
    const subtrust = suggestion.onboardingTestamentaryEntity;
    const display =
      entity?.displayName ??
      subtrust?.displayName ??
      sentenceCaseWithAcronyms(
        entity?.entityName || subtrust?.testamentaryEntityName || ''
      );
    const description = `the “${display}” ${entity ? 'entity' : 'subtrust'} suggestion`;
    options.push({
      value: suggestion.id,
      display,
      startAdornment: (
        <ListItemIcon>
          <Stars01Icon size={16} color={COLORS.TEAL[600]} />
        </ListItemIcon>
      ),
      endAdornment: (
        <Tooltip
          title={`Accepts ${description}`}
          onMouseDown={(event: MouseEvent) => {
            // Keep focus in the typeahead when the user clicks help.
            event.preventDefault();
          }}
          onClick={(event: MouseEvent) => event.stopPropagation()}
        >
          <HelpCircleIcon size={16} color={COLORS.TEXT.SECONDARY} />
        </Tooltip>
      ),
      disabled,
      onSelectAction: () => {
        void accept(description);
      },
    });
  }

  return { options, creating, selectionRevision };
}

function useAcceptSuggestedRecipient({
  householdId,
  household,
  suggestion,
  onCreated,
}: {
  householdId: string;
  household: AiOnboardingModal_HouseholdFragment | undefined;
  suggestion: AiSuggestionForReviewCard | undefined;
  onCreated: (id: string) => void;
}) {
  const { principalNodesById, loading: principalsLoading } =
    useOnboardingPrincipalNodes({
      householdId,
      suggestions: suggestion ? [suggestion] : [],
    });
  const [create] = useAiOnboardingModalBulkCreateMutation({
    refetchQueries: [
      DispositiveProvisionsPossibleRecipientsDocument,
      DispositiveProvisionsSuggestedRecipientsDocument,
    ],
    awaitRefetchQueries: true,
  });
  const pending = useRef(false);
  const [creating, setCreating] = useState(false);
  const [selectionRevision, setSelectionRevision] = useState(0);
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();

  async function accept(description: string) {
    if (pending.current || !household || !suggestion || principalsLoading)
      return;
    pending.current = true;
    setCreating(true);
    try {
      const form = dataToForm([suggestion], household, { principalNodesById });
      let missingFields: string[] = [];
      if (form.entities[0]) {
        missingFields = getMissingRequiredEntityFields(form.entities[0]);
      } else if (form.testamentaryEntities[0]) {
        missingFields = getMissingRequiredTestamentaryEntityFields(
          form.testamentaryEntities[0]
        );
      }
      const validationMessage =
        getSuggestedRecipientValidationMessage(missingFields);
      if (validationMessage) {
        showFeedback(validationMessage);
        return;
      }
      const { acceptedSuggestionIDs, ...input } = formDataToInput(
        form,
        ['entities', 'testamentaryEntities'],
        household,
        {
          [suggestion.id]: suggestion,
        }
      );
      // The onboarding mapper omits rows with missing required fields. Never
      // acknowledge a suggestion unless its record will also be created.
      if (!acceptedSuggestionIDs.includes(suggestion.id)) {
        showFeedback(
          'This suggestion has missing or invalid information. Go to the Estates page to resolve and accept the suggestion, then return here.'
        );
        return;
      }
      const result = await create({
        variables: {
          ...input,
          acknowledgeSuggestionsInput: [
            {
              suggestionID: suggestion.id,
              status: AiSuggestionAcceptanceStatus.Accepted,
            },
          ],
        },
      });
      // Only one suggestion is submitted, so at most one of these lists has a record.
      const id =
        result.data?.createEntities[0]?.id ??
        result.data?.createTestamentaryEntities[0]?.id;
      if (!id || result.errors?.length)
        throw new Error('Suggested recipient was not created');
      onCreated(id);
      showFeedback(`Accepted ${description}.`, { variant: 'success' });
    } catch (error) {
      showFeedback(
        'Could not accept the suggested recipient. Please try again.'
      );
      reportError(
        'could not create suggested DP recipient',
        error instanceof Error ? error : undefined
      );
    } finally {
      pending.current = false;
      setCreating(false);
      // Reset the typeahead's search text to the saved field value, including
      // when creation fails without changing the selected recipient ID.
      setSelectionRevision((revision) => revision + 1);
    }
  }

  return {
    accept,
    creating,
    disabled: creating || principalsLoading,
    selectionRevision,
  };
}
